// Recording one dose: two taps, no typing.
//
// Tap Give on a due dose -> this sheet -> Confirm. Everything the record needs
// is derived (time = now, dose = the ordered count and medicine, outcome,
// reaction = none), so the nurse verifies rather than types — the sheet IS the
// five-rights check, which is why this is two taps and not one. Withheld and
// Refused sit alongside Confirm: they are completions with a different outcome,
// not a separate flow, so they go through the same endpoint.
//
// The form behind it is tenant-seeded and the backend re-validates it, so
// derivation must degrade safely: anything required that we could not derive
// forces the full form open and blocks Confirm until it is filled.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Button, Chip, Collapse, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, MenuItem, Stack, TextField, Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { toast } from "react-toastify";

import { fulfillCarePlanTask, administerMedicationNow } from "../api/nurseApi";
import { NURSE_COLORS } from "../theme";
import { fieldsOf, firstMissingRequired, serialize, FieldInput } from "./fulfillmentForm";
import {
  GIVE_ACTIONS, deriveFulfillmentData, drugLabel, doseRouteLabel,
  PRE_RECEIPT_SOURCES, PRE_RECEIPT_SOURCE_KEY,
} from "../medicationDisplay";
import { formatIstDateTime } from "../../../utils/istDateTime";
import { unitsToDraw } from "../wardStock";

const fmtWhen = (d) => formatIstDateTime(d);

export default function MedicationAdministrationSheet({ open, task, performedById, stock, onClose, onRecorded }) {
  const fields = useMemo(() => fieldsOf(task?.fulfillment_form), [task]);
  const [outcome, setOutcome] = useState("given");
  const [values, setValues] = useState({});
  const [reason, setReason] = useState("");
  const [source, setSource] = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Units this dose takes off the ward. Defaults to the ordered per-round count
  // and stays editable — a half tablet or a double dose must be correctable
  // without the nurse retyping the whole form.
  const [units, setUnits] = useState("1");

  const taskId = task?.id;
  // Destructured to primitives: the caller builds `stock` with stockFor() inside
  // its render, so the object has a new identity every render and depending on
  // it directly would re-fire effects endlessly.
  const stockState = stock?.state;
  const stockOnHand = stock?.onHand;

  // The ordered count for this round, as a string for the text field. Absent on
  // STAT/interval doses, where one unit is the only sane default. Derived as a
  // primitive so the reset effect below does not have to depend on `task`,
  // which is a fresh object on every render.
  const orderedUnits = useMemo(() => {
    const v = task?.data?.slot_dose;
    return v === undefined || v === null || v === "" ? "1" : String(v);
  }, [task]);

  // Reseed whenever a new dose opens, or the nurse switches outcome — the
  // outcome changes which option the status field should carry.
  useEffect(() => {
    if (!open) return;
    setValues(deriveFulfillmentData(task, fields, outcome));
  }, [open, taskId, fields, outcome, task]);

  useEffect(() => {
    if (!open) return;
    setOutcome("given");
    setReason("");
    setSource("");
    setShowDetails(false);
    setUnits(orderedUnits);
  }, [open, taskId, orderedUnits]);

  const missing = firstMissingRequired(fields, values);

  // A field we could not derive means the seed differs from what we know how to
  // fill. Show the form rather than failing the submit server-side.
  useEffect(() => {
    if (open && missing) setShowDetails(true);
  }, [open, missing]);

  // An empty bin means this dose came from somewhere else. Put the source
  // picker in front of the nurse rather than making them go looking for it —
  // it is what decides whether the bin is decremented at all.
  useEffect(() => {
    if (open && stockState === "known" && stockOnHand <= 0) setShowDetails(true);
  }, [open, stockState, stockOnHand]);

  const setTop = useCallback((field, val) => setValues((s) => ({ ...s, [field]: val })), []);
  const setNested = useCallback(
    (parent, field, val) => setValues((s) => ({ ...s, [parent]: { ...(s[parent] || {}), [field]: val } })),
    []
  );

  // An ad-hoc dose (as-needed, STAT, extra) has no scheduled task behind it —
  // it is created and completed in one call. Everything else fulfills the task
  // it was opened from.
  const isAdHoc = task?.id == null && task?.prescription_id != null;

  const submit = async () => {
    if (task?.id == null && !isAdHoc) {
      toast.error("This dose has no ID and can't be recorded.");
      return;
    }
    if (missing) {
      toast.error(`Please fill: ${missing}`);
      setShowDetails(true);
      return;
    }
    setSubmitting(true);
    try {
      const data = serialize(fields, values);
      // serialize() only emits the tenant-seeded form fields, and
      // fulfill_care_plan_task REPLACES task.data wholesale — so anything not
      // written here is lost. slot_dose was being lost on every completed dose,
      // which made the backend's administered_units fall back to 1.0 per dose.
      const drawn = Number(units) || 0;
      data.slot_dose = drawn;                                     // clinical record
      data.ward_stock_units = unitsToDraw(outcome, source, drawn); // stock draw
      // Extra key alongside the seeded form's own fields — same key the old
      // mandatory prompt wrote, so historical records stay comparable.
      if (source) data[PRE_RECEIPT_SOURCE_KEY] = source;
      // Withholding or refusing is a clinical decision — keep the nurse's words
      // with the record. Only appended when the seeded form has no better home
      // for it, so a tenant with a dedicated reason field is not double-written.
      if (reason.trim() && outcome !== "given") {
        data.observations = [data.observations, reason.trim()].filter(Boolean).join(" — ");
      }
      const res = isAdHoc
        ? await administerMedicationNow({ prescription_id: task.prescription_id, data })
        : await fulfillCarePlanTask({
          task_id: task.id,
          performed_by_id: performedById,
          action: "complete",
          data,
        });
      // Flat envelope — a 200 can still be "Failed".
      if (res?.status === "Success") {
        toast.success(outcome === "given" ? "Dose recorded." : `Recorded as ${outcome}.`);
        onRecorded?.();
        onClose();
      } else {
        toast.error(res?.message || "Could not record this dose.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not record this dose.");
    } finally {
      setSubmitting(false);
    }
  };

  const patient = task?.patient;
  const now = new Date();

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="overline" color="text.secondary" sx={{ display: "block", lineHeight: 1.4 }}>
          Record dose
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
          {drugLabel(task)}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        <Stack spacing={0.75}>
          {patient?.name && (
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {patient.name}{patient.bed_code ? ` · ${patient.bed_code}` : ""}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">{doseRouteLabel(task)}</Typography>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }} useFlexGap>
            {task?.time_slot_label && <Chip size="small" label={task.time_slot_label} />}
            <Typography variant="caption" color="text.secondary">
              Administering now — {fmtWhen(now)}
            </Typography>
          </Stack>
        </Stack>

        {/* Ward stock. Hidden entirely when the count is unknowable or the
            pharmacy has not dispensed yet — an absent block is honest, a zero
            is not. NEVER gates Confirm: a stock count must not stop a patient
            being medicated. */}
        {stockState === "known" && (
          <Box
            sx={{
              mt: 2, p: 1.5, borderRadius: 2,
              border: `1px solid ${stockOnHand <= 0 ? NURSE_COLORS.warning : NURSE_COLORS.border}`,
              bgcolor: stockOnHand <= 0 ? "#FFFBEB" : NURSE_COLORS.pageBg,
            }}
          >
            <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
              <TextField
                size="small" type="number" label="Uses" sx={{ width: 100 }}
                inputProps={{ min: 0, step: "any" }}
                value={units}
                onChange={(e) => setUnits(e.target.value)}
              />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {stockOnHand} left in ward stock
                </Typography>
                {stockOnHand <= 0 && (
                  <Typography variant="caption" sx={{ color: NURSE_COLORS.warningInk }}>
                    No ward stock left — recording this dose anyway.
                  </Typography>
                )}
              </Box>
            </Stack>
          </Box>
        )}

        {outcome !== "given" && (
          <TextField
            size="small" fullWidth multiline minRows={2} sx={{ mt: 2 }}
            label={`Reason ${outcome === "withheld" ? "withheld" : "refused"}`}
            placeholder="e.g. patient NPO for theatre, BP too low"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        )}

        <Button
          size="small"
          sx={{ mt: 1.5 }}
          onClick={() => setShowDetails((v) => !v)}
          endIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        >
          {showDetails ? "Hide details" : "Add details"}
        </Button>
        {missing && (
          <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
            This ward's record needs {missing} before this dose can be saved.
          </Typography>
        )}

        <Collapse in={showDetails}>
          <Divider sx={{ my: 1.5 }} />
          <TextField
            select size="small" fullWidth sx={{ mb: 1.75 }}
            label="Source of this dose (optional)"
            helperText="Where the medicine came from — ward stock, the patient's own supply, etc."
            value={source}
            onChange={(e) => setSource(e.target.value)}
          >
            <MenuItem value="">— Not recorded —</MenuItem>
            {PRE_RECEIPT_SOURCES.map((s) => (
              <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
            ))}
          </TextField>
          {fields.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No form is configured for this dose. Recording it will save who gave it and when.
            </Typography>
          ) : (
            <Stack spacing={1.75}>
              {fields.map((f) =>
                f.type === "object" ? (
                  <Box key={f.field} sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>{f.field}</Typography>
                    <Stack spacing={1.5}>
                      {(f.structure || []).map((sub) => (
                        <FieldInput
                          key={sub.field}
                          def={sub}
                          value={(values[f.field] || {})[sub.field]}
                          onChange={(field, val) => setNested(f.field, field, val)}
                        />
                      ))}
                    </Stack>
                  </Box>
                ) : (
                  <FieldInput key={f.field} def={f} value={values[f.field]} onChange={setTop} />
                )
              )}
            </Stack>
          )}
        </Collapse>
      </DialogContent>

      {/* Outcome is a SELECTION, and one button submits whatever is selected.
          Selecting and submitting in a single handler would submit the previous
          outcome's derived values — the state has not re-derived yet. */}
      <DialogActions sx={{ px: 2.5, pb: 2, pt: 1.5, gap: 1 }}>
        {GIVE_ACTIONS.filter((a) => !a.primary).map((a) => (
          <Button
            key={a.key}
            size="small"
            color={outcome === a.key ? "warning" : "inherit"}
            variant={outcome === a.key ? "contained" : "outlined"}
            disabled={submitting}
            onClick={() => setOutcome(outcome === a.key ? "given" : a.key)}
          >
            {a.label}
          </Button>
        ))}
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          color={outcome === "given" ? "primary" : "warning"}
          disabled={submitting || Boolean(missing)}
          onClick={submit}
        >
          {submitting
            ? "Recording…"
            : outcome === "given" ? "Confirm" : `Save as ${outcome}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
